import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Mic, MicOff, Play, RotateCcw, Square } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FormData } from "../types";

interface PronunciationStepProps {
  formData: FormData;
  onRecordingChange: (blob: Blob | null, url: string) => void;
}

const PronunciationStep = ({ formData, onRecordingChange }: PronunciationStepProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const audioUrl = URL.createObjectURL(audioBlob);
        onRecordingChange(audioBlob, audioUrl);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playRecording = () => {
    if (formData.pronunciationRecordingUrl && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const resetRecording = () => {
    if (formData.pronunciationRecordingUrl) {
      URL.revokeObjectURL(formData.pronunciationRecordingUrl);
    }
    onRecordingChange(null, "");
  };

  return (
    <motion.div
      key="pronunciation"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Mic className="w-8 h-8 text-primary" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Name Pronunciation
        </h2>
        <p className="text-muted-foreground">
          Record yourself saying the names so we get the pronunciation right.
        </p>
      </div>

      <div className="space-y-4">
        <Label>🎤 Record name pronunciation *</Label>
        
        <div className="bg-muted/50 rounded-xl p-6 text-center space-y-4">
          {!formData.pronunciationRecordingUrl ? (
            <>
              <Button
                type="button"
                size="lg"
                variant={isRecording ? "destructive" : "default"}
                onClick={isRecording ? stopRecording : startRecording}
                className="w-full max-w-xs"
              >
                {isRecording ? (
                  <>
                    <Square className="w-5 h-5 mr-2" />
                    Stop Recording
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5 mr-2" />
                    Start Recording
                  </>
                )}
              </Button>
              
              {isRecording && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center justify-center gap-2"
                >
                  <span className="w-3 h-3 bg-destructive rounded-full animate-pulse" />
                  <span className="text-sm text-muted-foreground">Recording...</span>
                </motion.div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <audio
                ref={audioRef}
                src={formData.pronunciationRecordingUrl}
                onEnded={handleAudioEnded}
              />
              
              <div className="flex items-center justify-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={playRecording}
                  disabled={isPlaying}
                >
                  <Play className="w-4 h-4 mr-2" />
                  {isPlaying ? "Playing..." : "Play Recording"}
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetRecording}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Re-record
                </Button>
              </div>
              
              <p className="text-sm text-primary font-medium">
                ✓ Recording saved
              </p>
            </div>
          )}
        </div>

        <div className="text-sm text-muted-foreground space-y-1">
          <p>In one recording, please say all names exactly as they should be spoken in the song.</p>
          <p>Say each name slowly and clearly, pausing briefly between names.</p>
          <p>You may re-record if needed.</p>
        </div>
      </div>
    </motion.div>
  );
};

export default PronunciationStep;
